export type StaffRole = 'owner' | 'admin' | 'staff' | 'sales';
export type ClientRole = 'client_admin' | 'client_member' | 'client_viewer';
export type Role = StaffRole | ClientRole;
export type Stage = 'lead' | 'prospect' | 'active' | 'past' | 'disqualified';
export type ProjectState = 'discovery' | 'design' | 'spec' | 'build' | 'live' | 'closed';

export interface Me {
  id: string;
  email: string;
  name: string;
  role: Role;
  is_staff: boolean;
  client: { id: string; name: string } | null;
  totp_enabled: boolean;
  capabilities: string[];
  emails: { key: string; label: string; hint: string; on: boolean }[];     // email notifications, on or off
}

export interface UserRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: 'active' | 'disabled';
  is_staff: boolean;
  client: { id: string; name: string } | null;
  totp_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface Invitation {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  client: { id: string; name: string } | null;
  invited_by: string | null;
  created_at: string;
  expires_at: string;
}

export interface Client {
  id: string;
  name: string;
  slug: string;
  stage: Stage;
  website: string | null;
  domains: string[];
  client_can_triage: boolean;
  created_at: string;
  updated_at: string;
}

export interface AuditEvent {
  id: number;
  at: string;
  actor: string;
  actor_id: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  client_id: string | null;
  summary: string;
  changes: Record<string, [unknown, unknown]> | null;
  ip: string | null;
  user_agent: string | null;
}

export interface Product {
  id: string;
  client_id: string;
  name: string;
  description: string;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  client: { id: string; name: string };
  name: string;
  slug: string;
  summary: string;
  state: ProjectState;
  triage_allowed: boolean;
  products: { id: string; name: string }[];
  archived: boolean;
  created_at: string;
  updated_at: string;
  // Blue Market only
  path?: string;
  client_visible?: boolean;
  client_can_triage?: boolean | null;
}

export type RoundVisibility = 'internal' | 'ready' | 'approved';

export interface Job {
  id: string;
  status: 'queued' | 'running' | 'ok' | 'failed';
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  pdf: boolean;
  log?: string;
}

export interface Round {
  number: number;
  label: string;
  visibility: RoundVisibility;
  shared_at: string | null;
  approved_at: string | null;
  built: boolean;
  built_at: string | null;
  screen_count: number;
  pdf: boolean;
  open_comments: number;
  open_comments_by_screen: Record<string, number>;
  // Blue Market only
  stale?: boolean;
  pdf_stale?: boolean;
  build?: Job | null;
  claude_working?: boolean;
  kit?: { kit_id: string | null; name: string; slug: string; version: number; status: string | null } | null;
  discard_blocked?: string | null;        // latest round only: why it can't be discarded (null: it can)
}

// parent/label: a variant (a tab or state of its main screen). Variants come straight after their main screen.
export interface Screen { id: string; title: string; frame: 'phone' | 'desktop'; notes_html: string; parent?: string | null; label?: string | null; theme?: string | null; theme_name?: string | null }

export interface RoundDetail extends Round {
  screens: Screen[];
  intro_html: string;
  closing: { heading: string; html: string }[];
}

export interface Comment {
  id: string;
  screen_id: string | null;
  parent_id: string | null;
  author: { name: string; is_staff: boolean; me: boolean; via_link?: boolean };
  body: string;
  deleted: boolean;
  internal: boolean;
  resolved: boolean;
  resolved_at: string | null;
  created_at: string;
  edited: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_resolve: boolean;
  body_html: string;
  attachments?: { id: string; name: string; url: string; is_image: boolean; size: number }[];
  ticket?: { id: string; number: number; url: string } | null;     // the ticket this comment became
  idea?: { id: string; status: IdeaStatus } | null;     // Blue Market only
}

export interface CommentList { comments: Comment[]; can_comment: boolean; can_comment_internal: boolean; internal_only: boolean; can_make_ideas?: boolean; can_make_tickets?: boolean }

export type IdeaStatus = 'open' | 'submitted' | 'done' | 'dismissed';

export interface Idea {
  id: string;
  round: number;
  screen_id: string | null;
  body: string;
  body_html: string;
  status: IdeaStatus;
  created_by: string;
  created_at: string;
  submitted_at: string | null;
  submitted_round: number | null;
  comment: { id: string; author: string; via_link: boolean; round: number | null; screen_id: string | null } | null;
}

export interface NextRound {
  latest_round: number | null;
  next_exists: boolean;
  screens: Record<string, string>;
  ideas: Idea[];
  pending_batches: number;
  feedback_file: string | null;
  run: ClaudeRun | null;
  busy: ClaudeKind | null;
}

export type ReviewLinkState = 'unused' | 'claimed' | 'expired' | 'revoked';

export interface ReviewLink {
  id: string;
  label: string;
  reviewer_name: string | null;
  claimed_at: string | null;
  round_pin: number | null;
  created_by: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  last_seen_at: string | null;
  state: ReviewLinkState;
  comment_count: number;
  answers_questions: boolean;
}

export interface ReviewSettings { review_round: number | null; shareable_rounds: number[]; links: ReviewLink[] }

/** What someone with a review link sees. */
export interface ReviewerState {
  name: string;
  project_id: string;
  project: string;
  client: string;
  summary: string;
  expires_at: string;
  round: RoundDetail | null;
  questions: { total: number; open: number } | null;     // null: this link doesn't answer questions
}

export const PROJECT_STATE_LABELS: Record<ProjectState, string> = {
  discovery: 'Discovery', design: 'Design', spec: 'Spec', build: 'Build', live: 'Live', closed: 'Closed',
};
export const PROJECT_STATE_BADGE: Record<ProjectState, string> = {
  discovery: '', design: 'info', spec: 'info', build: 'warn', live: 'ok', closed: '',
};

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner', admin: 'Admin', staff: 'Staff', sales: 'Sales',
  client_admin: 'Client admin', client_member: 'Client member', client_viewer: 'Client viewer',
};

export const STAGE_LABELS: Record<Stage, string> = {
  lead: 'Lead', prospect: 'Prospect', active: 'Active client', past: 'Past client', disqualified: 'Disqualified',
};

export type ClaudeKind = 'spec' | 'handoff' | 'design-review' | 'kit';

export interface ClaudeRun {
  id: string;
  kind: ClaudeKind;
  status: Job['status'];
  round: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  log: string;
  summary_html: string;
  instructions: string;
  change: { number: number; step: 'draft' | 'apply' | 'package'; from: number | null } | null;     // change request runs
}

export interface OpenQuestion { key: string; topic: string; text: string; assumed: string; source: 'notes' | 'spec'; settled: 'answer' | 'assumption' | null }

export interface SpecHandoff {
  busy: ClaudeKind | null;
  round: { number: number; visibility: RoundVisibility } | null;
  questions: OpenQuestion[];
  open_questions: number;
  stack: Record<string, string>;
  spec: { exists: boolean; updated_at: string | null; older_than_designs: boolean; decisions_changed: boolean };
  handoff: { round: number; name: string; updated_at: string; size: number; older_than_spec: boolean } | null;
  run: ClaudeRun | null;
  changes: Changes | null;        // once the spec exists
}

export type ChangeStatus = 'draft' | 'approved' | 'rejected' | 'applied';
export interface ScreenRef { id: string; title: string; parent: string | null }
export interface ScreenDiff {
  from: number; to: number; added: ScreenRef[]; removed: ScreenRef[];
  changed: (ScreenRef & { parts: ('design' | 'notes')[] })[]; unchanged: number; shared: string[];
}
export interface ChangeRequest {
  number: number; title: string; from: number; to: number; status: ChangeStatus; note: string | null;
  drafted_at: string | null; approved_at: string | null; approved_by: string | null; rejected_at: string | null; applied_at: string | null;
  package: { name: string; size: number; updated_at: string } | null;
}
export interface Changes {
  spec_round: number | null;      // the round the spec describes
  rounds: { number: number; visibility: RoundVisibility }[];      // built rounds after it, newest first
  suggested: number | null; diff: ScreenDiff | null; next_number: number; items: ChangeRequest[];
}
export interface ChangeDoc extends SpecDoc { change: ChangeRequest; spec_round: number | null }

export interface BriefDoc { text: string; html: string; version: string | null; updated_at: string | null }
export interface Brief {
  brief: BriefDoc; concept: BriefDoc & { written: boolean };
  files: { name: string; size: number; updated_at: string | null }[]; extensions: string[]; max_mb: number;
  has_rounds: boolean; blocked: string | null; run: ClaudeRun | null; busy: ClaudeKind | null;
}

export interface SpecDoc { exists: boolean; text: string; html: string; version: string | null; updated_at: string | null }

export interface QuestionAnswer { kind: 'answer' | 'assumption'; answer: string; date: string; round: string }

export interface QuestionAsked { text: string; asked_by: string; asked_at: string; answered_by: string | null; answered_at: string | null; via_link: boolean }

export interface Question {
  key: string; topic: string; text: string; assumed: string; source: 'notes' | 'spec' | 'decisions';
  answer: QuestionAnswer | null;
  client: QuestionAsked | null;      // asked of the client
}

export interface Questions { round: number | null; questions: Question[]; resolved: Question[]; open: number; assumptions: number; asked: number; client_answered: number }

/** A question as the client sees it. */
export interface ClientQuestion { key: string; topic: string; text: string; asked_at: string; answer: string | null; answered_by: string | null; answered_at: string | null }

export interface ClientQuestions { questions: ClientQuestion[]; open: number; can_answer: boolean }

// ---------- tickets ----------

export type StatusCategory = 'todo' | 'doing' | 'done';
export type TicketPriority = 'none' | 'low' | 'medium' | 'high' | 'urgent';
export type TicketKind = 'task' | 'bug' | 'feature' | 'question';

export interface TicketStatus { id: string; name: string; category: StatusCategory; position: number }
export interface TicketSection { id: string; name: string; kind: 'backlog' | 'sprint' | 'other'; position: number; starts_on: string | null; ends_on: string | null; closed_at: string | null }
export interface Person { id: string; name: string; is_staff: boolean }

export interface Ticket {
  id: string;
  number: number;
  title: string;
  kind: TicketKind;
  priority: TicketPriority;
  status_id: string;
  section_id: string | null;
  rank: number;
  due_on: string | null;
  assignees: Person[];
  created_by: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  has_description: boolean;
  can_edit_text: boolean;
  comment_count: number;
  created_by_client: boolean;
  // Blue Market only
  internal?: boolean;
  estimate_hours?: number | null;
  cost_cents?: number | null;
}

export interface TicketDetail extends Ticket {
  description: string;
  description_html: string;
  project: { id: string; name: string };
  can: TicketPowers;
  statuses: TicketStatus[];
  sections: TicketSection[];
  people: Person[];
}

export interface TicketPowers { create: boolean; triage: boolean; manage: boolean }

export interface Board { statuses: TicketStatus[]; sections: TicketSection[]; tickets: Ticket[]; people: Person[]; can: TicketPowers; triage_allowed: boolean }

export interface ProjectSummary {
  latest_round: { number: number; visibility: RoundVisibility; open_comments: number } | null;
  rounds: number;
  tickets: { open: number; doing: number; done: number; overdue: number } | null;
  questions?: { open: number; asked: number; client_answered: number };
  ideas_open?: number;
  review_links?: number;
  client_questions?: { open: number; total: number };
}

export interface FileRef { id: string; name: string; url: string; is_image: boolean; size: number }

export interface TicketComment {
  id: string;
  author: { name: string; is_staff: boolean; me: boolean };
  body: string;
  body_html: string;
  internal: boolean;
  created_at: string;
  edited: boolean;
  deleted: boolean;
  can_edit: boolean;
  can_delete: boolean;
  attachments: FileRef[];
}

export interface TicketComments {
  comments: TicketComment[];
  can_comment: boolean;
  can_comment_internal: boolean;
  description_attachments: FileRef[];
  mentionable: { id: string; name: string; is_staff: boolean }[];
}

export interface AppNotification { id: string; kind: string; title: string; excerpt: string; url: string; actor: string; created_at: string; read: boolean }

export interface TicketDesign {
  id: string; round: number; screen_id: string | null; title: string; frame: 'phone' | 'desktop' | null;
  screen_url: string | null; viewer_url: string; newer: number | null; newer_url: string | null; from_comment: boolean;
}

export interface TicketDesigns {
  designs: TicketDesign[];
  can_link: boolean;
  choices: { round: number; visibility: RoundVisibility; screens: { id: string; title: string; frame: 'phone' | 'desktop' }[] }[];
}

export interface RoundTicket { id: string; number: number; title: string; status: string; category: StatusCategory; url: string }

export interface Filters {
  status: string[]; assignee: string[]; priority: TicketPriority[]; kind: TicketKind[];
  due: '' | 'overdue' | 'week' | 'none' | 'set'; client: boolean; internal: '' | 'only' | 'hide';
}
export interface ViewConfig { view: 'board' | 'list'; section: string; query: string; mine: boolean; show_done: boolean; show_closed: boolean; filters: Filters }
export interface SavedView { id: string; name: string; shared: boolean; mine: boolean; owner: string; config: Partial<ViewConfig>; updated_at: string }

// ---------- CRM ----------

export type Consent = 'unknown' | 'granted' | 'refused' | 'withdrawn';
export interface Contact {
  id: string; name: string; title: string; email: string | null; phone: string | null; linkedin: string | null; notes: string; source: string;
  primary: boolean; marketing_consent: Consent; consent_at: string | null; consent_note: string; archived: boolean; created_by: string; created_at: string; updated_at: string;
}
export type ActivityKind = 'note' | 'call' | 'meeting' | 'email';
export interface Activity {
  id: string; kind: ActivityKind; occurred_at: string; subject: string; body: string; body_html: string; author: string; edited: boolean;
  contacts: { id: string; name: string }[]; can_edit: boolean;
}

export interface CrmTask {
  id: string; client_id: string; title: string; notes: string; due_on: string | null;
  assignee: { id: string; name: string } | null; contact: { id: string; name: string } | null;
  remind_at: string | null; reminded: boolean; done: boolean; done_at: string | null; done_by: string | null; created_by: string; created_at: string;
  client?: { id: string; name: string };
}

export type OppStage = 'qualifying' | 'proposal' | 'negotiation' | 'won' | 'lost';
export interface Opportunity {
  id: string; client_id: string; title: string; value_cents: number | null; stage: OppStage; probability: number; expected_close: string | null;
  notes: string; lost_reason: string; owner: { id: string; name: string } | null; closed_at: string | null; project_id: string | null; rank: number;
  created_by: string; created_at: string; updated_at: string; client?: { id: string; name: string; stage: Stage };
}
